// Hotel search as URL query values, shared by the search widget, results and checkout pages.
// Query: dest (destination key from the suggestions, e.g. "GOA, INDIA") and name (display name;
// searched by name when dest is missing), checkin, checkout, rooms (one entry per room: adults,
// then ":" and each child's age joined by "." — "2,1:6.9" is 2 adults; 1 adult with children of 6 and 9),
// nat (lead guest's nationality, ISO code). Older links with city/adults/children still work.
import { addDaysIso, daysBetween, fmtDay, todayIso } from '../../shared/data/common';

export const MAX_ROOMS = 5;
export const MAX_ADULTS = 6;
export const MAX_CHILDREN = 4;
export const MAX_NIGHTS = 30;

/** "2,1:6.9" → [{ adults: 2, childAges: [] }, { adults: 1, childAges: [6, 9] }]. */
export function parseRooms(value) {
  return String(value || '')
    .split(',')
    .map((room) => {
      const [adults, ages = ''] = room.split(':');
      return {
        adults: Math.min(MAX_ADULTS, Math.max(1, Number(adults) || 0)),
        childAges: ages.split('.').filter((a) => a !== '').map(Number).filter((a) => Number.isInteger(a) && a >= 0 && a <= 17).slice(0, MAX_CHILDREN),
      };
    })
    .filter((room) => room.adults > 0)
    .slice(0, MAX_ROOMS);
}

export const roomsParam = (rooms) => rooms.map((r) => (r.childAges.length ? `${r.adults}:${r.childAges.join('.')}` : `${r.adults}`)).join(',');

/** Rooms from older links: rooms=2&adults=4&children=1 spread as evenly as possible. */
function legacyRooms(q) {
  const count = Math.min(MAX_ROOMS, Math.max(1, Number(q.rooms) || 1));
  const adults = Math.max(count, Number(q.adults || q.guests) || 2);
  const children = Math.max(0, Number(q.children) || 0);
  return Array.from({ length: count }, (_, i) => ({
    adults: Math.min(MAX_ADULTS, Math.floor(adults / count) + (i < adults % count ? 1 : 0)),
    childAges: Array.from({ length: Math.floor(children / count) + (i < children % count ? 1 : 0) }, () => 8).slice(0, MAX_CHILDREN),
  }));
}

/** Search values from URL query parameters, with defaults for anything missing. */
export function parseHotelQuery(q) {
  const checkin = q.checkin && q.checkin >= todayIso() ? q.checkin : addDaysIso(7);
  const checkout = q.checkout && q.checkout > checkin ? q.checkout : addDaysIso(2, checkin);
  // Older links count guests in adults/guests; new links describe each room in rooms.
  const rooms = q.adults || q.guests ? legacyRooms(q) : parseRooms(q.rooms);
  return {
    dest: (q.dest || '').trim(),
    name: (q.name || q.city || '').trim(),
    checkin,
    checkout,
    rooms: rooms.length ? rooms : [{ adults: 2, childAges: [] }],
    nat: /^[A-Za-z]{2}$/.test(q.nat || '') ? q.nat.toUpperCase() : 'IN',
  };
}

/** URL query values for a search. */
export function toHotelParams(s) {
  return {
    ...(s.dest ? { dest: s.dest } : {}),
    name: s.name,
    checkin: s.checkin,
    checkout: s.checkout,
    rooms: roomsParam(s.rooms),
    ...(s.nat && s.nat !== 'IN' ? { nat: s.nat } : {}),
  };
}

export const hotelQueryString = (s) => new URLSearchParams(toHotelParams(s)).toString();

/** API search request once the destination key is known. */
export const toSearchRequest = (s, destination) => ({
  destination,
  checkIn: s.checkin,
  checkOut: s.checkout,
  rooms: s.rooms.map((r) => ({ adults: r.adults, childAges: r.childAges })),
  nationality: s.nat,
});

/** Problems the API would reject, caught before searching. */
export function searchProblem(s) {
  if (!s.dest && !s.name) return 'Choose where you want to stay.';
  const nights = daysBetween(s.checkin, s.checkout);
  if (nights < 1) return 'Check-out must be after check-in.';
  if (nights > MAX_NIGHTS) return `Stays can be booked for up to ${MAX_NIGHTS} nights.`;
  return null;
}

export const nightsOf = (s) => daysBetween(s.checkin, s.checkout);
export const guestCount = (rooms) => rooms.reduce((n, r) => n + r.adults + r.childAges.length, 0);
const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;
export const nightsLabel = (n) => plural(n, 'night');

/** "2 rooms · 3 adults, 1 child". */
export function roomsLabel(rooms) {
  const adults = rooms.reduce((n, r) => n + r.adults, 0);
  const children = rooms.reduce((n, r) => n + r.childAges.length, 0);
  return `${plural(rooms.length, 'room')} · ${plural(adults, 'adult')}${children ? `, ${plural(children, 'child', 'children')}` : ''}`;
}

export const datesLabel = (s) => `${fmtDay(s.checkin)} – ${fmtDay(s.checkout)}`;

/** First part of a full destination name: "Goa, India" → "Goa". */
export const shortName = (name) => String(name || '').split(',')[0].trim();
